// Herramientas en el TELÉFONO, dentro de Crear
// (ADR-2026-10-30-herramientas-se-abre-como-catalogo): la card que la lista entre los destinos
// (`CardDeHerramientas`) y la grilla que abre, con las mismas cards que Redes sociales y
// Catálogo (`GrillaDeHerramientas`). Era una pantalla completa que abría la pestaña de la barra
// (ADR-2026-10-09-herramientas-en-el-telefono-a-pantalla-completa); esa pestaña ahora es Publicar.
// Qué va y en qué orden lo arma `herramientasDeLaPantalla`; acá sólo se dibuja.

import type { EdicionSheet, ModoSheet } from './SheetGenerar';
import { setupAutoplayVideo } from './HomeCards';
import { herramientasDeLaPantalla, type HerramientaMobile } from './herramientasMobile';
import { bajadaCortaModo } from '../modesCatalog';

/**
 * La imagen de la herramienta, con el lenguaje del abanico de las cards por destino de la
 * home (`AbanicoDeResultados`): una FICHA inclinada —borde blanco, esquinas redondeadas,
 * sombra— apoyada sobre el tinte. Las ilustraciones de los atajos (fondo transparente) ya
 * vienen compuestas como fichas, así que van sueltas, sin marco.
 *
 * `ancho` y `alto` son los de la ficha, en px; `giro`, su inclinación.
 */
function Ficha({
  herramienta: h,
  ancho,
  alto,
  giro,
}: {
  herramienta: HerramientaMobile;
  ancho: number;
  alto: number;
  giro: number;
}) {
  const { medio } = h;
  if (medio.tipo === 'imagen' && medio.ajuste === 'contain') {
    if (!medio.src) return null;
    // Del tamaño de la ficha (la ilustración trae su propio aire transparente) y con la mitad
    // del giro: ya viene compuesta, sólo acompaña la inclinación del resto.
    return (
      <img
        src={medio.src}
        alt=""
        loading="lazy"
        className="block object-contain drop-shadow-[0_8px_14px_rgba(16,24,45,0.14)]"
        style={{ width: ancho, height: alto, transform: `rotate(${giro / 2}deg)` }}
      />
    );
  }
  return (
    <span
      className="relative block overflow-hidden rounded-[14px] bg-white shadow-[0_10px_24px_rgba(16,24,45,0.18)] ring-[3px] ring-white"
      style={{ width: ancho, height: alto, transform: `rotate(${giro}deg)` }}
    >
      {medio.tipo === 'dibujo' ? (
        // La carátula dibujada (Estudio) tiene tipografía fija: se dibuja al doble y se
        // achica, así se ve igual que en grande en vez de romper renglones.
        <span
          className="absolute left-0 top-0 block origin-top-left"
          style={{ width: ancho * 2, height: alto * 2, transform: 'scale(0.5)' }}
        >
          <medio.Dibujo />
        </span>
      ) : medio.tipo === 'video' ? (
        <video
          ref={setupAutoplayVideo}
          src={medio.src}
          poster={medio.poster}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <img src={medio.src} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      )}
    </span>
  );
}

/**
 * La card de Herramientas en Crear (ADR-2026-10-30-publicar-reemplaza-a-herramientas-en-la-barra):
 * su lugar en la barra pasó a Publicar, y Crear la suma después de los destinos. Misma forma que
 * `CardDestinoMobile` —texto a la izquierda, dos fichas inclinadas a la derecha— con las
 * carátulas de las dos primeras herramientas. Tocarla abre esta pantalla.
 */
export function CardDeHerramientas({
  ediciones,
  modos,
  onClick,
}: {
  ediciones: EdicionSheet[];
  modos: ModoSheet[];
  onClick: () => void;
}) {
  const fichas = herramientasDeLaPantalla(ediciones, modos).principales.slice(0, 2);
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex min-h-[148px] w-full items-center overflow-hidden rounded-[20px] border border-black/[0.06] bg-[#EDEFF5] p-4 text-left transition-transform active:scale-[0.99]"
    >
      <span className="relative z-10 flex w-[56%] flex-col">
        <span className="text-[18px] font-bold leading-tight tracking-[-0.02em] text-[#16181D]">Herramientas</span>
        <span className="mt-1 text-[12.5px] leading-snug text-black/55">Editá, animá y terminá tus fotos y videos.</span>
      </span>
      {/* Del tamaño de las fichas de las otras cards de Crear (el abanico a escala 0.75). */}
      <span className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center">
        {fichas.map((h, i) => (
          <span key={h.id} className={i === 0 ? 'relative z-0 -mr-5 mt-5' : 'relative z-10 -mt-5'}>
            <Ficha herramienta={h} ancho={70} alto={88} giro={i === 0 ? -6 : 5} />
          </span>
        ))}
      </span>
    </button>
  );
}

/**
 * Herramientas como sección del sheet de Crear, con las MISMAS cards que Redes sociales y
 * Catálogo (`GrillaDeModos`): carátula 3:2, nombre y bajada debajo, de a dos
 * (ADR-2026-10-30-herramientas-se-abre-como-catalogo). Las herramientas mezclan modos y
 * ediciones rápidas, que `GrillaDeModos` no sabe dibujar, así que acá se repite su anatomía
 * con los medios de `herramientasDeLaPantalla`. El orden es el de esa lista: las cuatro
 * principales primero.
 */
export function GrillaDeHerramientas({
  ediciones,
  modos,
  onElegir,
}: {
  ediciones: EdicionSheet[];
  modos: ModoSheet[];
  onElegir: (onAbrir: () => void) => void;
}) {
  const { principales, resto } = herramientasDeLaPantalla(ediciones, modos);
  // La bajada CORTA, como en las cards de Catálogo: la de la edición o la del modo. La
  // `explicacion` es la larga de la pantalla completa y en media columna se cortaba.
  const bajada = (h: HerramientaMobile) =>
    ediciones.find((e) => e.label === h.id)?.desc || bajadaCortaModo(h.id, '') || h.explicacion;
  return (
    <div className="grid grid-cols-2 gap-3 px-4">
      {[...principales, ...resto].map((h, i) => (
        <button
          key={h.id}
          type="button"
          onClick={h.soloEnComputadora ? undefined : () => onElegir(h.onAbrir)}
          disabled={h.soloEnComputadora}
          aria-label={h.bloqueado ? `${h.nombre} — ver planes` : h.nombre}
          // Escalonadas de a pares (una fila, 40ms después de la anterior): con las once de
          // golpe la entrada se sentía brusca (ADR-2026-10-30-el-sheet-de-crear-cambia-de-seccion-sin-saltos).
          style={{ animationDelay: `${60 + Math.min(Math.floor(i / 2), 5) * 40}ms` }}
          className="animate-ficha-de-pestana group flex flex-col overflow-hidden rounded-xl border border-black/[0.06] text-left transition-shadow hover:shadow-[0_2px_16px_rgba(0,0,0,0.09)]"
        >
          <span
            className={`relative block aspect-[3/2] overflow-hidden ${h.soloEnComputadora ? 'opacity-60' : ''}`}
            style={{ background: h.tinte }}
          >
            <MedioDeCard herramienta={h} />
            {h.bloqueado && (
              <>
                <span aria-hidden="true" className="absolute inset-0 bg-black/55" />
                <span
                  aria-hidden="true"
                  className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm"
                >
                  <span className="material-symbols-rounded text-[24px]">lock</span>
                </span>
              </>
            )}
          </span>
          <span className="block px-3.5 py-3">
            <span
              className={`text-[14px] font-semibold leading-snug ${h.soloEnComputadora ? 'text-black/45' : 'text-brand-black/90'}`}
              style={DOS_RENGLONES}
            >
              {h.nombre}
            </span>
            <span className="mt-0.5 text-[12.5px] leading-snug text-black/50" style={DOS_RENGLONES}>
              {h.soloEnComputadora ? 'Disponible en computadora' : bajada(h)}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** Hasta dos renglones y "…", como el título y la bajada de `GrillaDeModos`. */
const DOS_RENGLONES = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const;

/** La carátula de la card 3:2: la foto o el clip a sangre, la ilustración contenida sobre el
 *  tinte, y el Estudio dibujado al doble y achicado (su tipografía es fija). */
function MedioDeCard({ herramienta: { medio } }: { herramienta: HerramientaMobile }) {
  const sangre = 'absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]';
  if (medio.tipo === 'dibujo') {
    return (
      <span className="absolute left-0 top-0 block h-[200%] w-[200%] origin-top-left scale-50">
        <medio.Dibujo />
      </span>
    );
  }
  if (medio.tipo === 'video') {
    return (
      <video
        ref={setupAutoplayVideo}
        src={medio.src}
        poster={medio.poster}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        aria-hidden="true"
        className={sangre}
      />
    );
  }
  if (!medio.src) return null;
  return medio.ajuste === 'contain' ? (
    <img src={medio.src} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-contain p-2" />
  ) : (
    <img src={medio.src} alt="" aria-hidden="true" loading="lazy" className={sangre} />
  );
}
